import { useApp } from '../../context/AppContext';

const SHARE_FACTS = [
  "Sharing your commitments publicly makes you 3× more likely to follow through.",
  "If 1 billion people take practical action, it could reduce up to 20% of global emissions.",
  "Talking about climate action with friends is one of the highest-leverage actions you can take.",
];

export function Share() {
  const { userName, doneActions, totalCO2Saved, copied, setCopied, showToast } = useApp();

  const shareText = `🌍 I've committed to ${doneActions.length} green actions — potentially saving ${(totalCO2Saved/1000).toFixed(1)}t CO₂/yr! Check out the Green Action Plan: #ClimateAction`;

  const handleCopy = () => {
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast("Copied!", "📋");
    });
  };

  const handleTwitterShare = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `🌍 I've committed to ${doneActions.length} climate actions — saving ~${(totalCO2Saved/1000).toFixed(1)}t CO₂/yr! What's your green commitment? #ClimateAction #GreenPlan`
      )}`,
      "_blank"
    );
  };

  const handleWhatsAppShare = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(
        `🌍 I've committed to ${doneActions.length} green actions — saving ~${(totalCO2Saved/1000).toFixed(1)}t CO₂/yr! 🌱`
      )}`,
      "_blank"
    );
  };

  return (
    <div style={{ animation: "slideIn 0.3s ease" }}>
      <h2 style={{ fontSize: "1.6rem", fontWeight: "normal", color: "#f0fdf4", marginBottom: "0.3rem" }}>
        Share Your <em style={{ color: "#93c5fd" }}>Impact</em>
      </h2>
      <p style={{ color: "#6b7280", fontSize: "0.83rem", marginBottom: "1.5rem" }}>
        Climate action is contagious. Sharing your commitments can inspire others to act too.
      </p>

      {/* Preview card */}
      <div 
        style={{ 
          background: "linear-gradient(135deg, #0a1f0c, #0d1a2b)", 
          border: "1px solid rgba(34,197,94,0.22)", 
          borderRadius: "16px", 
          padding: "1.4rem", 
          marginBottom: "1.25rem" 
        }}
      >
        <div 
          style={{ 
            fontSize: "0.65rem", 
            letterSpacing: "0.25em", 
            color: "#4ade80", 
            textTransform: "uppercase", 
            marginBottom: "0.6rem" 
          }}
        >
          🌍 GreenPlan · My Commitments
        </div>
        <div style={{ fontSize: "1.2rem", color: "#f0fdf4", marginBottom: "0.4rem" }}>
          {userName || "I"}'ve committed to {doneActions.length} green action{doneActions.length !== 1 ? "s" : ""}
        </div>
        <div style={{ fontSize: "0.82rem", color: "#86efac", marginBottom: "1rem" }}>
          Potentially saving ~{(totalCO2Saved/1000).toFixed(1)}t CO₂ per year
        </div>
        {doneActions.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {doneActions.slice(0, 5).map(action => (
              <span
                key={action.id}
                style={{
                  fontSize: "0.7rem",
                  background: "rgba(34,197,94,0.1)",
                  border: "1px solid rgba(34,197,94,0.18)",
                  color: "#86efac",
                  padding: "0.18rem 0.45rem",
                  borderRadius: "99px",
                }}
              >
                {action.emoji} {action.title}
              </span>
            ))}
            {doneActions.length > 5 && (
              <span style={{ fontSize: "0.7rem", color: "#6b7280", padding: "0.18rem 0.45rem" }}>
                +{doneActions.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Share buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        <button
          onClick={handleCopy}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            width: "100%",
            background: "rgba(34,197,94,0.1)",
            border: "1px solid rgba(34,197,94,0.25)",
            color: "#86efac",
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            fontSize: "0.86rem",
            textAlign: "left",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
        >
          <span style={{ fontSize: "1.15rem" }}>📋</span>
          <div>
            <div style={{ fontWeight: 600 }}>{copied ? "Copied!" : "Copy share text"}</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.7, marginTop: "0.1rem" }}>
              Paste into WhatsApp, iMessage, etc.
            </div>
          </div>
        </button>

        <button
          onClick={handleTwitterShare}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            width: "100%",
            background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.2)",
            color: "#93c5fd",
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            fontSize: "0.86rem",
            textAlign: "left",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
        >
          <span style={{ fontSize: "1.15rem" }}>𝕏</span>
          <div>
            <div style={{ fontWeight: 600 }}>Share on X / Twitter</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.7, marginTop: "0.1rem" }}>
              Opens pre-filled tweet
            </div>
          </div>
        </button>

        <button
          onClick={handleWhatsAppShare}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            width: "100%",
            background: "rgba(37,211,102,0.08)",
            border: "1px solid rgba(37,211,102,0.18)",
            color: "#86efac",
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            fontSize: "0.86rem",
            textAlign: "left",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
        >
          <span style={{ fontSize: "1.15rem" }}>💬</span>
          <div>
            <div style={{ fontWeight: 600 }}>Share on WhatsApp</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.7, marginTop: "0.1rem" }}>
              Opens WhatsApp with message
            </div>
          </div>
        </button>
      </div>

      {/* Facts */}
      <div 
        style={{ 
          marginTop: "1.5rem", 
          padding: "1rem", 
          background: "rgba(255,255,255,0.03)", 
          borderRadius: "12px" 
        }}
      >
        <div 
          style={{ 
            fontSize: "0.68rem", 
            letterSpacing: "0.15em", 
            color: "#6b7280", 
            textTransform: "uppercase", 
            marginBottom: "0.65rem" 
          }}
        >
          Did you know?
        </div>
        {SHARE_FACTS.map((fact, i) => (
          <div key={i} style={{ display: "flex", gap: "0.55rem", marginBottom: "0.55rem" }}>
            <span style={{ color: "#4ade80", flexShrink: 0 }}>🌿</span>
            <span style={{ color: "#9ca3af", fontSize: "0.8rem", lineHeight: 1.55 }}>
              {fact}
            </span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "1.1rem", fontSize: "0.65rem", color: "#6b7280", textAlign: "center" }}>
        Sources: World Resources Institute · UNEP · PNAS 2025 · Nature Human Behaviour 2025
      </div>
    </div>
  );
}
